import * as signalR from "@microsoft/signalr";
import { useEffect, useState } from "react";
import config from "@/config";
import { GetAllowedHubsByFeatures, getCookie } from "../generic.methods";
import {
  setSignalRConnectionStatus,
  getSignalRConnections,
} from "@/store/slices/general/signalRHubSlice";
import { useDispatch, useSelector } from "react-redux";
import { useRouter } from "next/router";
import storage from "../storage";
import FeatureService from "@/services/feature-service";
import { FeatureKeys, Enums } from "@/services/dropdown-enums";
import {
  selectFeatures,
  selectFeaturesLoaded,
} from "@/store/slices/features/featuresSlice";
import Report_Dashboard_signalr from "@/modules/crm/analytics/report-dashboard-signalr-constants";

const testingEmailsV2 = [
  // "sualeha.sehar@breakthru.pk",
  // "pakiza.munawar@breakthru.pk",
  // "hasan.khan@breakthru.pk",
  // "samad.ahmed@breakthru.pk",
];
const testingEmailsV3 = [
  // "rida.maqsood@breakthru.pk",
  // "break1thru@gmail.com",
  // "pakizasqa12@gmail.com",
  // "pakizatester123@gmail.com",
  // "hasanhukum251@gmail.com",
  // "samad.ahmed@breakthru.pk"
];
let ExceptVerioningHubs = []; // list of hubs which are not using versioning in url

const isWindow = typeof window !== "undefined" ? true : false;
if (isWindow) {
  window.hubs = {};
  window.hubStatuses = {};
  window._hubReconnectTimers = {}; // Track per-hub reconnect timers to prevent duplicates
}

const useSignalRClient = (props, baseUrl) => {
  const { hubName = undefined } = props || {};
  // Use Redux as the single source of truth for hub statuses.
  // This is globally reactive — every hook instance sees the same state.
  const statuses = useSelector(getSignalRConnections);
  const features = useSelector(selectFeatures);
  const featuresLoaded = useSelector(selectFeaturesLoaded);
  const [RTC, setRTC] = useState(isWindow ? window.hubs : {});
  const user = JSON.parse(storage.getItem("Profile"));
  const dispatch = useDispatch();
  const isSingleHub = typeof hubName === "string" ? true : false;
  const { query } = useRouter();
  let companyKey = props?.isPublic
    ? "" // query?.ref
    : getCookie("companyKey") || props?.companyKey || query?.companykey;

  const getHubBaseUrl = (name) => {
    if (baseUrl) return baseUrl;
    if (name === Report_Dashboard_signalr.hubName)
      return config.signalRReportUrl;
    return config.signalRUrl;
  };

  // DRY helper: sync hub state across window globals, React state, and Redux.
  const _syncHubState = (name, isConnected) => {
    if (!name) return;
    window.hubStatuses[name] = isConnected;
    dispatch(
      setSignalRConnectionStatus({ hubName: name, status: isConnected })
    );
    if (isConnected) setRTC({ ...window.hubs });
  };

  useEffect(() => {
    if (companyKey) {
      _createConnection(props?.isPublic);
    }
  }, [companyKey, featuresLoaded]);

  // Layer 3: Tab Visibility Lifecycle
  // When the user returns to an inactive tab, attempt .start() on any Disconnected hubs.
  // This catches cases where the browser paused JS timers during sleep/inactivity.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        Object.keys(window.hubs).forEach((name) => {
          const hubConn = window.hubs[name];
          if (hubConn && hubConn.state === "Disconnected") {
            console.log(`Tab active -> reconnecting ${name}`);
            hubConn
              .start()
              .then(() => _syncHubState(name, true))
              .catch((err) =>
                console.warn(`Visibility reconnect failed for ${name}`, err)
              );
          }
        });
      }
    };

    if (isWindow) {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }
    return () => {
      if (isWindow) {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
      }
    };
  }, []);

  const _createConnection = (publicConnection = false, cmkey = companyKey) => {
    if (isSingleHub) {
      let hub = createHubInfo(hubName);
      _initConnection(hub, publicConnection, cmkey);
    } else if (Array.isArray(hubName)) {
      // for initializing multiple hubs

      hubName.forEach((item) => {
        let hub = createHubInfo(item);
        _initConnection(hub, publicConnection, cmkey);
      });
    }
  };

  // Layer 2: scheduleReconnect fallback
  // After the 5-minute automatic retry expires, onclose fires.
  // This schedules a simple .start() retry every 5 seconds on the SAME object.
  // Using the same object preserves all .on() listeners registered by OnInitHub.
  const _scheduleReconnect = (hubName, connectedHub) => {
    // Prevent duplicate timers for the same hub
    if (window._hubReconnectTimers[hubName]) return;

    window._hubReconnectTimers[hubName] = setTimeout(async () => {
      window._hubReconnectTimers[hubName] = null;

      if (connectedHub.state === "Disconnected") {
        try {
          await connectedHub.start();
          console.log(`scheduleReconnect succeeded for ${hubName}`);
          _syncHubState(hubName, true);
        } catch (err) {
          console.warn(
            `scheduleReconnect failed for ${hubName}, retrying in 5s...`
          );
          _scheduleReconnect(hubName, connectedHub); // Queue next attempt
        }
      }
      // If state is Connected/Reconnecting, do nothing — another path handled it
    }, 5000);
  };

  const _initConnection = (hub, publicConnection, cmkey = companyKey) => {
    const isPublicPage = typeof window !== 'undefined' && (
      window.location.pathname.includes("book-an-appointment") ||
      window.location.pathname.includes("reschedule-an-appointment") ||
      window.location.pathname.includes("cancel-an-appointment")
    );

    if (isPublicPage && !publicConnection) {
      return;
    }

    let token = publicConnection ? "" : getCookie("CRM-Auth");
    let legacyAuth = getCookie("Auth") || "";
    let companyName = publicConnection ? "" : getCookie("companyName");
    let allowedHubs = [];
    const hasCRM = FeatureService.hasFeature(features, FeatureKeys.CRM);
    const hasAppointments = FeatureService.hasFeature(
      features,
      FeatureKeys.APPOINTMENT_SCHEDULING
    );

    if (hasCRM) {
      allowedHubs = GetAllowedHubsByFeatures(FeatureKeys.CRM, user);
    } else if (hasAppointments) {
      allowedHubs = GetAllowedHubsByFeatures(
        FeatureKeys.APPOINTMENT_SCHEDULING,
        user
      );
    }
    if (
      !cmkey ||
      (!publicConnection &&
        (!featuresLoaded || !allowedHubs.includes(hub.name)))
    ) {
      return;
    }
    // check if connection already build
    if (window.hubs[hub.name]) {
      // re initialize statuses state when hub is already connected.
      return;
    }

    let signalUrl = `${hub.url}?`;
    if (cmkey) {
      signalUrl = signalUrl + `companyKey=${cmkey}`;
      // `${props.isPublic ? "maskedcompanykey" : "companyKey"}=${cmkey}`;
    }
    if (companyName) {
      signalUrl = signalUrl + `&companyName=${companyName}`;
    }
    if (legacyAuth) {
      signalUrl = signalUrl + `&apexMainAuthToken=${legacyAuth}`;
    }

    // Create and configure the SignalR connection
    window.hubs[hub.name] = new signalR.HubConnectionBuilder()
      .withUrl(
        // `${hub.url}?companyKey=${companyKey}&companyName=${companyName}`,
        signalUrl,
        {
          skipNegotiation: true,
          transport: signalR.HttpTransportType.WebSockets,
          accessTokenFactory: () => `${token}`,
        }
      )
      // Layer 1: Custom exponential backoff — up to 5 minutes
      // Retries: 1s, 2s, 3s, 4s... capped at 10s. Gives up after 5 min total.
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          if (retryContext.elapsedMilliseconds < 300000) {
            return Math.min(
              1000 * (retryContext.previousRetryCount + 1),
              10000
            );
          }
          return null; // Stop after 5 min → triggers onclose → Layer 2 takes over
        },
      })
      .build();

    if (window.hubs[hub.name]) {
      let connectedHub = window.hubs[hub.name];
      connectedHub
        .start()
        .then(() => {
          console.log(`SignalR connection established for ${hub.name}`);
          _syncHubState(hub.name, true);
        })
        .catch((error) => {
          console.error(`Initial connection failed for ${hub.name}:`, error);
          _syncHubState(hub.name, false);
          // Initial .start() failure does NOT trigger onclose.
          // Manually schedule a retry on the same object.
          // _scheduleReconnect(hub.name, connectedHub);
        });

      // Callback for when the connection is successfully reconnected (within 5-min window)
      connectedHub.onreconnected((connectionId) => {
        onReconnected(hub.name, connectionId);
      });

      // Callback for when reconnection is attempted but hasn't succeeded yet
      connectedHub.onreconnecting((err) => {
        _syncHubState(hub.name, false);
        console.log("Reconnecting... ", hub.name, err);
      });

      // Layer 2: onclose fallback
      // Fires when: (a) automatic retry gives up after 5 min, or (b) server explicitly closes.
      // We call .start() on the SAME object — never destroy it.
      connectedHub.onclose((error) => {
        // null error = intentional .stop() call (e.g. logout). Don't resurrect.
        if (!error) {
          console.log(`Connection closed intentionally for ${hub.name}`);
          return;
        }

        console.warn(
          `Connection dead for ${hub.name}. Scheduling reconnect...`,
          error
        );
        _syncHubState(hub.name, false);
        // _scheduleReconnect(hub.name, connectedHub);
      });
    }
  };
  // onReconnected callback function
  const onReconnected = (hubName, id) => {
    console.log("reconnected", hubName, id);
    if (hubName)
      dispatch(setSignalRConnectionStatus({ hubName: hubName, status: true }));
  };

  const getVersion = () => {
    if (testingEmailsV2.includes(user?.Email)) {
      return `/v2`;
    } else if (testingEmailsV3.includes(user?.Email)) {
      return `/v3`;
    } else {
      return "";
    }
  };

  const createHubInfo = (name) => {
    let url = `${getHubBaseUrl(name)}/${name}`;
    let versioning = true;
    if (ExceptVerioningHubs.includes(name)) versioning = false;
    url = url + `${versioning ? getVersion() : ""}`;
    let hub = { name, url: url };
    return hub;
  };

  const _disconnectHub = (name = "") => {
    if (window.hubs[name]) {
      let hub = window.hubs[name];
      // Clear any pending reconnect timer for this hub
      if (window._hubReconnectTimers[name]) {
        clearTimeout(window._hubReconnectTimers[name]);
        delete window._hubReconnectTimers[name];
      }
      delete window.hubs[name];
      delete window.hubStatuses[name];
      hub.stop().then(() => console.log(`Connection stopped for ${name}`));
    }
  };

  const revokeHubChannels = () => {
    let hubs = Object.keys(window.hubs);
    if (hubs && hubs.length) {
      hubs.forEach((hub) => {
        _disconnectHub(hub);
      });
    }
  };

  return {
    revokeHubChannels,
    statuses,
    RTC,
    _createConnection,
  };
};

export default useSignalRClient;
